import { useId, type ReactNode } from 'react';
import { Avatar, Button, CountPill, VisuallyHidden } from '../primitives/primitives.js';
import { Checkbox } from '../forms/fields.js';
import { Icon } from '../icons/icons.js';
import styles from './grid.module.css';

/**
 * TESSMA ONE data grid.
 *
 * The table, tabs and pagination are FROZEN (§9) and transcribed exactly.
 * Row selection, the bulk-action toolbar and the page-size selector are D-8
 * additions, frozen in v1.1.
 */

export function GridFrame({ children }: { readonly children: ReactNode }) {
  return <div className={styles.frame}>{children}</div>;
}

export function GridToolbar({ children }: { readonly children: ReactNode }) {
  return <div className={styles.toolbar}>{children}</div>;
}

export function GridToolbarSpacer() {
  return <span className={styles.toolbarSpacer} />;
}

export function GridScroll({ children }: { readonly children: ReactNode }) {
  return <div className={styles.scroll}>{children}</div>;
}

export function Table({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label: string;
}) {
  return (
    <table className={styles.table} aria-label={label}>
      {children}
    </table>
  );
}

export type SortDirection = 'asc' | 'desc' | null;

export interface ThProps {
  readonly children: ReactNode;
  readonly align?: 'left' | 'right';
  /** Present makes the header a real sort button with `aria-sort`. */
  readonly sort?: { readonly direction: SortDirection; readonly onSort: () => void };
  readonly className?: string;
}

export function Th({ children, align = 'left', sort, className }: ThProps) {
  const classes = [styles.th, align === 'right' ? styles.thRight : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  const ariaSort =
    sort === undefined
      ? undefined
      : sort.direction === 'asc'
        ? 'ascending'
        : sort.direction === 'desc'
          ? 'descending'
          : 'none';

  return (
    <th scope="col" className={classes} aria-sort={ariaSort}>
      {sort === undefined ? (
        children
      ) : (
        <button type="button" className={styles.sortButton} onClick={sort.onSort}>
          {children}
          <span
            className={[styles.sortGlyph, sort.direction !== null ? styles.sortActive : '']
              .filter(Boolean)
              .join(' ')}
            aria-hidden="true"
          >
            <Icon
              name={
                sort.direction === 'asc'
                  ? 'caret-up'
                  : sort.direction === 'desc'
                    ? 'caret-down'
                    : 'arrows-down-up'
              }
              size={11}
            />
          </span>
        </button>
      )}
    </th>
  );
}

export function Tr({
  children,
  selected = false,
}: {
  readonly children: ReactNode;
  readonly selected?: boolean;
}) {
  return (
    <tr
      className={[styles.row, selected ? styles.rowSelected : ''].filter(Boolean).join(' ')}
      aria-selected={selected ? true : undefined}
    >
      {children}
    </tr>
  );
}

export interface TdProps {
  readonly children: ReactNode;
  readonly variant?: 'primary' | 'secondary' | 'money';
  readonly className?: string;
}

export function Td({ children, variant = 'primary', className }: TdProps) {
  const classes = [
    styles.td,
    variant === 'secondary' ? styles.tdSecondary : '',
    variant === 'money' ? styles.tdMoney : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <td className={classes}>{children}</td>;
}

/** D-8: selection cell. Uses the approved checkbox; the row is its label. */
export function SelectCell({
  checked,
  onChange,
  label,
  header = false,
  indeterminate = false,
}: {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: string;
  readonly header?: boolean;
  readonly indeterminate?: boolean;
}) {
  const control = (
    <Checkbox
      label={<VisuallyHidden>{label}</VisuallyHidden>}
      aria-label={label}
      checked={checked}
      indeterminate={indeterminate}
      onChange={(event) => onChange(event.currentTarget.checked)}
    />
  );
  return header ? (
    <th scope="col" className={`${styles.th} ${styles.selectCell}`}>
      {control}
    </th>
  ) : (
    <td className={`${styles.td} ${styles.selectCell}`}>{control}</td>
  );
}

export function OwnerCell({
  initials,
  name,
}: {
  readonly initials: string;
  readonly name: string;
}) {
  return (
    <span className={styles.owner}>
      <Avatar initials={initials} size={24} name={name} />
      <span className={styles.ownerName}>{name}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Tabs (frozen, §9)                                                   */
/* ------------------------------------------------------------------ */

export interface GridTabsProps<T extends string> {
  readonly label: string;
  readonly options: ReadonlyArray<{
    readonly value: T;
    readonly label: string;
    readonly count?: number;
  }>;
  readonly value: T;
  readonly onChange: (value: T) => void;
}

export function GridTabs<T extends string>({ label, options, value, onChange }: GridTabsProps<T>) {
  const activeIndex = options.findIndex((option) => option.value === value);

  return (
    <div
      className={styles.tabs}
      role="tablist"
      aria-label={label}
      onKeyDown={(event) => {
        const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (delta === 0) return;
        event.preventDefault();
        const next = options[(activeIndex + delta + options.length) % options.length];
        if (next !== undefined) onChange(next.value);
      }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={[styles.tab, selected ? styles.tabActive : ''].filter(Boolean).join(' ')}
            onClick={() => onChange(option.value)}
          >
            {option.label}
            {option.count !== undefined && <CountPill>{option.count}</CountPill>}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pagination (frozen) + page-size selector (D-8)                      */
/* ------------------------------------------------------------------ */

export interface PaginationProps {
  readonly page: number;
  readonly pageCount: number;
  readonly rangeLabel: string;
  readonly onPageChange: (page: number) => void;
  readonly pageSize?: number;
  readonly pageSizeOptions?: ReadonlyArray<number>;
  readonly onPageSizeChange?: (size: number) => void;
}

export function Pagination({
  page,
  pageCount,
  rangeLabel,
  onPageChange,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
}: PaginationProps) {
  const selectId = useId();
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);

  return (
    <nav className={styles.pagination} aria-label="Pagination">
      <span className={styles.paginationCount}>{rangeLabel}</span>

      {pageSize !== undefined && onPageSizeChange !== undefined && (
        <span className={styles.pageSize}>
          <label htmlFor={selectId}>Rows</label>
          <span className={styles.pageSizeWrap}>
            <select
              id={selectId}
              className={styles.pageSizeSelect}
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.currentTarget.value))}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className={styles.pageSizeCaret} aria-hidden="true">
              <Icon name="caret-down" size={11} />
            </span>
          </span>
        </span>
      )}

      <span className={styles.paginationControls}>
        <button
          type="button"
          className={styles.pageCell}
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <Icon name="caret-left" size={12} />
        </button>
        {pages.map((number) => (
          <button
            key={number}
            type="button"
            className={[styles.pageCell, number === page ? styles.pageCurrent : '']
              .filter(Boolean)
              .join(' ')}
            aria-label={`Page ${String(number)}`}
            aria-current={number === page ? 'page' : undefined}
            onClick={() => onPageChange(number)}
          >
            {number}
          </button>
        ))}
        <button
          type="button"
          className={styles.pageCell}
          aria-label="Next page"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          <Icon name="caret-right" size={12} />
        </button>
      </span>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* D-8 bulk-action toolbar                                             */
/* ------------------------------------------------------------------ */

export interface BulkActionBarProps {
  readonly count: number;
  readonly onClear: () => void;
  readonly children: ReactNode;
}

export function BulkActionBar({ count, onClear, children }: BulkActionBarProps) {
  return (
    <div className={styles.bulkBar} role="status">
      <span className={styles.bulkCount}>{count} selected</span>
      <Button variant="tertiary" onClick={onClear}>
        Clear selection
      </Button>
      <span className={styles.bulkActions}>{children}</span>
    </div>
  );
}
